/**
 * FlashcardText component for displaying Spanish text with word-level highlighting
 * Supports target verb highlighting and word-level selection
 */

interface WordToken {
  text: string
  position: number
  isVerb: boolean
}

interface FlashcardTextProps {
  lines: string[]
  targetVerb?: string
  className?: string
}

export function FlashcardText({ lines, targetVerb, className = '' }: FlashcardTextProps) {
  // Track if we've already found the first verb occurrence
  let foundVerb = false

  // Parse lines into word tokens, preserving spaces
  const parseWords = (text: string): WordToken[] => {
    // Split on word boundaries but preserve spaces
    const tokens: WordToken[] = []
    const words = text.split(/(\s+)/)

    words.forEach((segment, index) => {
      // Skip empty segments
      if (segment.length === 0) return

      // If it's whitespace, add it as-is (not a verb)
      if (/^\s+$/.test(segment)) {
        tokens.push({
          text: segment,
          position: index,
          isVerb: false,
        })
      } else {
        // It's a word - check if it's the target verb (exact match only, no substrings)
        const cleanWord = segment.replace(/[.,!?;:()[\]{}'"]/g, '').toLowerCase().trim()
        const isVerb = targetVerb && !foundVerb
          ? cleanWord === targetVerb.toLowerCase()
          : false

        // If we found the verb, mark it so subsequent occurrences aren't highlighted
        if (isVerb) {
          foundVerb = true
        }

        tokens.push({
          text: segment,
          position: index,
          isVerb,
        })
      }
    })

    return tokens
  }

  return (
    <div className={`font-mono ${className}`}>
      {lines.map((line, lineIndex) => {
        const words = parseWords(line)

        return (
          <div key={lineIndex} className="mb-2 last:mb-0 leading-relaxed">
            {words.map((word, wordIndex) => (
              <span
                key={`${lineIndex}-${wordIndex}`}
                className={
                  word.isVerb
                    ? 'text-blue-600 font-bold bg-blue-100 px-1 rounded'
                    : 'text-gray-900'
                }
              >
                {word.text}
              </span>
            ))}
          </div>
        )
      })}
    </div>
  )
}

